import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AppLayout } from './components/layout/AppLayout';
import { Dashboard } from './pages/Dashboard';
import { CheckIns } from './pages/CheckIns';
import { NewCheckIn } from './pages/check-ins/NewCheckIn';
import { CheckInDetails } from './pages/check-ins/CheckInDetails';
import { CheckInReport } from './pages/check-ins/report/[report-slug]';
import { Settings } from './pages/Settings';
import { Toaster } from '@/components/ui/sonner';
import { useState, useEffect } from 'react';
import { useMixpanelPageview } from '@/hooks/use-mixpanel-pageview';
import { ApolloProvider } from '@apollo/client';
import apolloClient from './lib/apolloClient';

// Auth pages
import { Login } from './pages/auth/Login';
import { Register } from './pages/auth/Register';
import { ResetPassword } from './pages/auth/ResetPassword';
import { UpdatePassword } from './pages/auth/UpdatePassword';
import { AuthCallback } from './pages/auth/AuthCallback';
import { AcceptInvite } from './pages/auth/AcceptInvite';
import { VerifyEmail } from './pages/auth/VerifyEmail';
import { DeleteAccount } from './pages/DeleteAccount';
import { AuthProvider } from './contexts/AuthContext';
import { AuthGuard } from './components/AuthGuard';
import { OrganizationGuard } from './components/OrganizationGuard';
// Import the new check-in token page
import CheckInToken from './pages/check-ins/[token]';

function MixpanelPageviewTracker() {
  useMixpanelPageview();
  return null;
}

function App() {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  // Close mobile menu when window is resized to larger viewport
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1024) {
        setIsMobileMenuOpen(false);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <ApolloProvider client={apolloClient}>
      <AuthProvider>
        <BrowserRouter>
          <MixpanelPageviewTracker />
          <Routes>
            {/* Auth routes - available only when not authenticated */}
            <Route element={<AuthGuard requireAuth={false} />}>
              <Route path="/auth/login" element={<Login />} />
              <Route path="/auth/register" element={<Register />} />
              <Route path="/auth/reset-password" element={<ResetPassword />} />
              <Route path="/auth/update-password" element={<UpdatePassword />} />
              <Route path="/auth/callback" element={<AuthCallback />} />
              <Route path="/auth/accept-invite" element={<AcceptInvite />} />
              <Route path="/auth/verify-email" element={<VerifyEmail />} />
            </Route>
            
            {/* Check-in token routes (accessible without authentication) */}
            <Route path="/check-in/:token" element={<CheckInToken />} />
            <Route path="/checkin/:token" element={<CheckInToken />} />
            
            {/* Protected routes - require authentication and organization */}
            <Route element={<AuthGuard requireAuth={true} />}>
              <Route element={<OrganizationGuard />}>
                <Route 
                  element={
                    <AppLayout 
                      isMobileMenuOpen={isMobileMenuOpen} 
                      setIsMobileMenuOpen={setIsMobileMenuOpen} 
                    />
                  }
                >
                  <Route path="/" element={<Dashboard />} />
                  <Route path="/check-ins" element={<CheckIns />} />
                  <Route path="/check-ins/new" element={<NewCheckIn />} />
                  <Route path="/check-ins/:slug" element={<CheckInDetails />} />
                  <Route path="/check-ins/report/:slug" element={<CheckInReport />} />
                  <Route path="/settings" element={<Settings />} />
                  <Route path="/delete-account" element={<DeleteAccount />} />
                  <Route path="*" element={<Navigate to="/" replace />} />
                </Route>
              </Route>
            </Route>
          </Routes>
          <Toaster position="top-right" />
        </BrowserRouter>
      </AuthProvider>
    </ApolloProvider>
  );
}

export default App;